import { Button, Card } from "@rumbledirect/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, Input, message, Typography } from "antd";
import { useState } from "react";
import {
  Button as V2Button,
  buttonVariants,
  Card as V2Card,
  CardContent,
  CardHeader,
  CardTitle,
  Textarea,
} from "@rumbledirect/ui-v2";
import { useViewConfigurationClient } from "../client";

export interface ActionNoteField {
  label: string;
  required?: boolean;
}

export interface Action {
  text: string;
  url?: string;
  formId?: string;
  icon?: string;
  external?: boolean;
  submitEndpoint?: string;
  noteField?: ActionNoteField;
}

export interface ActionsCardProps {
  title: string;
  uiV2?: boolean;
  description?: string;
  image?: string;
  actions: Action[];
}

const useSubmitAction = (action: Action, notifyError: boolean) => {
  const axiosInstance = useViewConfigurationClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (note?: string) => {
      if (!action.submitEndpoint) return;
      const trimmedNote = note?.trim();
      return axiosInstance.post(
        action.submitEndpoint,
        trimmedNote ? { note: trimmedNote } : {},
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["view-configuration"] });
    },
    onError: () => {
      if (notifyError)
        message.error("Er is een fout opgetreden. Probeer het opnieuw.");
    },
  });
};

const V2ActionButton = ({ action }: { action: Action }) => {
  const submitMutation = useSubmitAction(action, false);
  const [note, setNote] = useState("");

  const submit = () =>
    submitMutation.mutate(note, { onSuccess: () => setNote("") });

  if (action.url) {
    return (
      <a
        href={action.url}
        target={action.external ? "_blank" : undefined}
        rel={action.external ? "noopener noreferrer" : undefined}
        className={`${buttonVariants()} w-full`}
      >
        {action.text}
      </a>
    );
  }

  const noteField = action.noteField;
  const missingNote = Boolean(noteField?.required) && note.trim() === "";

  return (
    <div className="space-y-3">
      {noteField && (
        <label className="flex flex-col gap-2 text-sm font-medium text-foreground">
          {noteField.label}
          <Textarea
            rows={4}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>
      )}
      <V2Button
        className="w-full"
        disabled={submitMutation.isPending || missingNote}
        onClick={submit}
      >
        {action.text}
      </V2Button>
      {submitMutation.isSuccess && (
        <p role="status" className="text-sm text-muted-foreground">
          Verstuurd.
        </p>
      )}
      {submitMutation.isError && (
        <p role="alert" className="text-sm text-destructive">
          Dit is niet gelukt. Probeer het opnieuw.
        </p>
      )}
    </div>
  );
};

const ActionButton = ({ action }: { action: Action }) => {
  const submitMutation = useSubmitAction(action, true);

  if (action.url) {
    return (
      <Button
        href={action.url}
        target={action.external ? "_blank" : undefined}
        rel={action.external ? "noopener noreferrer" : undefined}
        type="primary"
        style={{ width: "100%" }}
      >
        {action.text}
      </Button>
    );
  }

  if (action.submitEndpoint && action.noteField) {
    const { label, required } = action.noteField;

    return (
      <Form<{ note?: string }>
        layout="vertical"
        onFinish={(values) => submitMutation.mutate(values.note)}
      >
        <Form.Item
          name="note"
          label={label}
          rules={
            required
              ? [
                  {
                    required: true,
                    whitespace: true,
                    message: `${label} is verplicht`,
                  },
                ]
              : []
          }
        >
          <Input.TextArea rows={4} />
        </Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={submitMutation.isPending}
          style={{ width: "100%" }}
        >
          {action.text}
        </Button>
      </Form>
    );
  }

  if (action.submitEndpoint) {
    return (
      <Button
        type="primary"
        loading={submitMutation.isPending}
        onClick={() => submitMutation.mutate(undefined)}
        style={{ width: "100%" }}
      >
        {action.text}
      </Button>
    );
  }

  return null;
};

export const ActionsCard = ({
  title,
  description,
  image,
  actions,
  uiV2,
}: ActionsCardProps) => {
  if (uiV2) {
    return (
      <V2Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {image && <img src={image} alt={title} className="max-w-full" />}
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
          {actions.map((action) => (
            <V2ActionButton key={action.text} action={action} />
          ))}
        </CardContent>
      </V2Card>
    );
  }

  return (
    <Card title={title}>
      {image && <img src={image} alt={title} style={{ maxWidth: "100%" }} />}
      {description && (
        <Typography.Paragraph>{description}</Typography.Paragraph>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {actions.map((action) => (
          <ActionButton key={action.text} action={action} />
        ))}
      </div>
    </Card>
  );
};
